'use client';

import type React from 'react';
import { type ReactNode, useRef } from 'react';
import { ChevronLeft } from 'lucide-react';
import { css, cx } from '@/styled-system/css';
import { useIsDesktop } from '@/src/lib/hooks/ui/useIsDesktop';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { useScrollFadeHeader } from '@/src/components/features/property/hooks';
import { DetailPanelOutlet, DetailPanelProvider } from '@/src/components/features/property/ui/DetailPanel';
import { IconButton } from '@zillow/constellation-10';
import { LucideIcon } from '@/src/components/LucideIcon';
import {
  listingActionGlass,
  listingActionGlassForeground,
} from '@/src/components/features/listings/ListingActionCluster/listingActionCluster.styles';
import { ExpandedContainer } from '@/src/components/features/property/layouts/ExpandedContainer';
import { searchContentBodyGutter } from '@/src/layouts/searchContentGutters.styles';
import { DesktopStickyHeader } from './DesktopStickyHeader';
import * as styles from './listingPage.styles';

type BackNav = {
  href: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  label?: string;
};

type MobileHeaderActionVariant = 'onMedia' | 'onSurface';

const mobileFloatingFooterGlass = css(listingActionGlass.raw({ shape: 'topRounded' }));
const desktopFloatingFooterGlass = css(listingActionGlass.raw({ shape: 'rounded' }));
const floatingFooterForeground = css(listingActionGlassForeground);

export type ListingPageProps = {
  /** The full listing being rendered. Fetched by the caller (HdpPage). */
  listing: PropertyDetailListing;
  /** Override back-link clicks. Without this, anchors navigate to backHref normally. */
  onBack?: () => void;
  /** Crawlable href for the header back links. Defaults to '/'. */
  backHref?: string;
  /** Photo gallery slot (HALO: PhotoGallerySection). */
  photoGallery?: ReactNode;
  /** Main module list slot (HALO: ModuleRenderer output). */
  modules: ReactNode;
  /** Footer slot (HALO: PageFooter). */
  pageFooter?: ReactNode;
  /** Mobile top-right action cluster (HALO: ListingHeaderActions). */
  listingHeaderActions?: ReactNode | ((options: { mobileFloatingVariant: MobileHeaderActionVariant }) => ReactNode);
  /** Desktop sticky header right-rail actions (ListingHeaderActions showCTAs). */
  desktopHeaderActions?: ReactNode;
  /** Desktop side-panel right rail actions. */
  detailPanelActions?: ReactNode;
  /** Mobile sticky bottom CTA bar (HALO: SpokeFooterCTAs). */
  spokeFooterCTAs?: ReactNode;
  /** Desktop floating bottom CTA bar. */
  desktopFooterCTAs?: ReactNode;
};

/**
 * ListingPage — HDP shell.
 *
 * Accepts render-slot props so each sub-area (gallery, modules, actions,
 * footer) can be composed from whatever the caller has ready. Listing data
 * lives as a prop — fetched once by HdpPage and threaded in.
 *
 * Intentional omissions (follow-up work):
 *   - HaloListingHead → generateMetadata wiring (awaits SSR prefetch)
 *   - BDP-back navigation + legacy scroll restore (not applicable to App Router)
 *   - Analytics dispatches (trackListingView / clientProfiler)
 *   - Mobile ImmersiveContainer variant — dormant
 */
export function ListingPage({
  listing,
  onBack,
  backHref,
  photoGallery,
  modules,
  pageFooter,
  listingHeaderActions,
  desktopHeaderActions,
  detailPanelActions,
  spokeFooterCTAs,
  desktopFooterCTAs,
}: ListingPageProps) {
  const mainContentRef = useRef<HTMLDivElement>(null);
  const photoGalleryRef = useRef<HTMLDivElement>(null);
  const isMobile = !useIsDesktop();

  const { backgroundOpacity } = useScrollFadeHeader({
    enabled: true,
    scrollContainerRef: undefined,
    exitTargetRef: photoGalleryRef,
    useWindowScroll: true,
  });

  const handleBackClick = onBack
    ? (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
          return;
        }

        e.preventDefault();
        onBack();
      }
    : undefined;

  return (
    <ExpandedContainer>
      <DetailPanelProvider isMobile={isMobile} scrollContainerRef={mainContentRef}>
        <ExpandedContent
          isMobile={isMobile}
          mainContentRef={mainContentRef}
          photoGalleryRef={photoGalleryRef}
          backgroundOpacity={backgroundOpacity}
          back={{ href: backHref ?? '/', onClick: handleBackClick }}
          listing={listing}
          photoGallery={photoGallery}
          modules={modules}
          pageFooter={pageFooter}
          listingHeaderActions={listingHeaderActions}
          desktopHeaderActions={desktopHeaderActions}
          detailPanelActions={detailPanelActions}
          spokeFooterCTAs={spokeFooterCTAs}
          desktopFooterCTAs={desktopFooterCTAs}
        />
      </DetailPanelProvider>
    </ExpandedContainer>
  );
}

type ExpandedContentProps = {
  isMobile: boolean;
  mainContentRef: React.RefObject<HTMLDivElement | null>;
  photoGalleryRef: React.RefObject<HTMLDivElement>;
  backgroundOpacity: number;
  back: BackNav;
  listing: PropertyDetailListing;
  photoGallery?: ReactNode;
  modules: ReactNode;
  pageFooter?: ReactNode;
  listingHeaderActions?: ReactNode | ((options: { mobileFloatingVariant: MobileHeaderActionVariant }) => ReactNode);
  desktopHeaderActions?: ReactNode;
  detailPanelActions?: ReactNode;
  spokeFooterCTAs?: ReactNode;
  desktopFooterCTAs?: ReactNode;
};

function ExpandedContent({
  isMobile,
  mainContentRef,
  photoGalleryRef,
  backgroundOpacity,
  back,
  listing: _listing,
  photoGallery,
  modules,
  pageFooter,
  listingHeaderActions,
  desktopHeaderActions,
  detailPanelActions,
  spokeFooterCTAs,
  desktopFooterCTAs,
}: ExpandedContentProps) {
  const mobileFloatingVariant: MobileHeaderActionVariant = backgroundOpacity > 0 ? 'onSurface' : 'onMedia';
  const listingHeaderActionsContent =
    typeof listingHeaderActions === 'function' ? listingHeaderActions({ mobileFloatingVariant }) : listingHeaderActions;

  return (
    <div className={styles.detailHubContainer} data-hdp-page>
      <header className={styles.detailHeader}>
        <IconButton
          asChild
          shape="circle"
          tone="neutral"
          emphasis="bare"
          size="sm"
          title={back.label ?? 'Back to search results'}
          aria-label={back.label ?? 'Back to search results'}
          css={listingActionGlass.raw({ layout: 'control' })}
        >
          <a href={back.href} onClick={back.onClick} data-testid="link-back-to-search-results-mobile">
            <LucideIcon icon={ChevronLeft} css={listingActionGlassForeground} />
          </a>
        </IconButton>
        <div />
        <div className={styles.headerActions}>{listingHeaderActionsContent}</div>
      </header>

      <main className={styles.detailMainContent}>
        <DesktopStickyHeader
          backHref={back.href}
          onBackClick={back.onClick}
          backLabel={back.label}
          actions={desktopHeaderActions}
        />
        {!isMobile && desktopFooterCTAs ? (
          <footer className={styles.desktopFloatingFooter}>
            <div className={cx(styles.floatingFooterSurface, desktopFloatingFooterGlass)}>
              <div className={cx(styles.desktopFooterActions, floatingFooterForeground)}>{desktopFooterCTAs}</div>
            </div>
          </footer>
        ) : null}
        <div ref={mainContentRef as React.Ref<HTMLDivElement>} className={styles.detailScrollArea}>
          <div className={styles.detailBody}>
            {photoGallery ? <div ref={photoGalleryRef}>{photoGallery}</div> : null}
            <div className={searchContentBodyGutter}>
              {modules}
              {pageFooter}
            </div>
          </div>
        </div>
      </main>

      {!isMobile && <DetailPanelOutlet headerActions={detailPanelActions} />}

      {isMobile && spokeFooterCTAs ? (
        <footer className={styles.detailFooter}>
          <div className={cx(styles.floatingFooterSurface, mobileFloatingFooterGlass)}>
            <div className={cx(styles.spokeFooterActions, floatingFooterForeground)}>{spokeFooterCTAs}</div>
          </div>
        </footer>
      ) : null}
    </div>
  );
}

export default ListingPage;
